import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack
} from '@mui/material';
import { useSnackbar } from 'notistack';
import sucursalesVentaService, { SucursalVenta } from '../../services/sucursalesVentaService';

const defaultForm: SucursalVenta = {
  nroSuc: 0,
  nombreSucursal: '',
  iniciarEn: '',
  tipoSuc: ''
};

const SucursalVentaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroSuc } = useParams();
  const [formData, setFormData] = useState<SucursalVenta>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSucursal = useCallback(async () => {
    if (!nroSuc || nroSuc === 'nuevo') return;
    try {
      setLoading(true);
      const sucursal = await sucursalesVentaService.getSucursalVenta(Number(nroSuc));
      setFormData({
        nroSuc: sucursal.nroSuc,
        nombreSucursal: sucursal.nombreSucursal ?? '',
        iniciarEn: sucursal.iniciarEn ?? '',
        tipoSuc: sucursal.tipoSuc ?? ''
      });
    } catch (error) {
      enqueueSnackbar('Error al cargar la sucursal', { variant: 'error' });
      navigate('/configuraciones/sucursalesventa');
    } finally {
      setLoading(false);
    }
  }, [nroSuc, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroSuc && nroSuc !== 'nuevo') loadSucursal();
  }, [nroSuc, loadSucursal]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nroSuc') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nombreSucursal.trim()) {
        enqueueSnackbar('El nombre de la sucursal es requerido', { variant: 'warning' });
        return;
      }

      if (nroSuc && nroSuc !== 'nuevo') {
        await sucursalesVentaService.updateSucursalVenta(Number(nroSuc), formData);
      } else {
        await sucursalesVentaService.createSucursalVenta(formData);
      }

      enqueueSnackbar('Sucursal guardada exitosamente', { variant: 'success' });
      navigate('/configuraciones/sucursalesventa');
    } catch (error: any) {
      console.error('Error al guardar sucursal:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la sucursal', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {nroSuc && nroSuc !== 'nuevo' ? 'Editar Sucursal' : 'Nueva Sucursal'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <TextField 
                  name="nroSuc" 
                  label="N° Sucursal" 
                  type="number"
                  value={formData.nroSuc} 
                  onChange={handleChange} 
                  fullWidth 
                  required 
                  disabled={!!(nroSuc && nroSuc !== 'nuevo')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField 
                  name="nombreSucursal" 
                  label="Nombre" 
                  value={formData.nombreSucursal} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="iniciarEn" 
                  label="Iniciar en" 
                  value={formData.iniciarEn || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="tipoSuc" 
                  label="Tipo Sucursal" 
                  value={formData.tipoSuc || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/configuraciones/sucursalesventa')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SucursalVentaForm;
